/**
 * The palette (design spec §3.1).
 *
 * "Mud, slate, moss, bone, iron — with colour reserved for meaning." The
 * restraint is the design: if grass is vivid green, then blood is just another
 * bright thing on a bright screen. Everything the valley is made of sits in a
 * narrow, desaturated band so that the few saturated hues — blood, fire, the
 * Cŵn Annwn's red ears — read instantly as *significant* rather than as
 * decoration.
 *
 * Saturated colour is spent, not scattered. There are exactly three places it
 * is allowed, and they are all listed under `SIGNAL`.
 */

import { TILE_KINDS, type TileKind } from '@annwn/protocol';

import { glyph } from '@annwn/content';

import type { Rgb } from './renderer.js';

/** The valley's colours. Desaturated on purpose. */
export const EARTH = {
  night: 0x0b0c0e,
  peat: 0x1a1712,
  mud: 0x3a3128,
  slate: 0x3f464c,
  stone: 0x5a5f63,
  moss: 0x4a5a3a,
  grass: 0x5d6b45,
  bone: 0xc9c3b2,
  iron: 0x8a9099,
  water: 0x2c4450,
  waterlight: 0x3d5f70,
  reed: 0x6b6a44,
  bark: 0x4a3b2c,
  leaf: 0x3f5236,

  // M3-01. Every one of these sits in the same narrow, desaturated band as the
  // originals — the point of ten more terrains is that the valley *changes* as
  // you walk through it, not that the screen gets more colourful.
  brine: 0x1e3038,
  sand: 0x6d6555,
  shingle: 0x565046,
  saltgrass: 0x54613f,
  bracken: 0x5a4a30,
  oak: 0x36452c,
  hazel: 0x3d4c2e,
  peatwater: 0x24201a,
  heather: 0x4a4152,
  track: 0x4c4133,
  ash: 0x2b2724,
} as const;

/**
 * Colour that means something. Everything here is saturated, and nothing else
 * in the game is allowed to be.
 */
export const SIGNAL = {
  blood: 0x8e1c1c,
  fire: 0xd98324,
  otherworld: 0xe8e8f0,
} as const;

export interface TileLook {
  readonly glyph: number;
  readonly fg: Rgb;
  readonly bg: Rgb;

  /**
   * Two to four alternative glyphs, picked by position (M3-01).
   *
   * The Qud trick, and it is worth the field. A hundred tiles of oakwood all
   * drawing `"` reads as a texture swatch; the same hundred drawing `"`, `,`
   * and `'` in a fixed but unpredictable arrangement reads as ground. The pick
   * is a hash of the coordinate rather than a random draw, so a tile looks the
   * same every frame, on every client, forever — the alternative is a valley
   * that shimmers when you walk.
   */
  readonly variants?: readonly number[];
}

/**
 * Which glyph a tile draws at a coordinate.
 *
 * The hash is the same integer mix worldgen scatters trees with, for the same
 * reason: two adjacent tiles must not correlate, and the answer must not depend
 * on anything but the coordinate.
 */
export function tileGlyph(look: TileLook, x: number, y: number): number {
  const variants = look.variants;
  if (!variants || variants.length === 0) return look.glyph;

  let h = Math.imul(x | 0, 0x27d4eb2d);
  h = Math.imul(h ^ Math.imul(y | 0, 0x85ebca6b), 0x165667b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491);
  h ^= h >>> 16;
  return variants[(h >>> 0) % variants.length] as number;
}

/**
 * How each terrain draws. Terrain is mostly *background* with a sparse glyph
 * over it — a field of `"` characters reads as noise, while a field of colour
 * with the occasional tuft reads as ground.
 */
export const TILE_LOOKS: { readonly [K in TileKind]: TileLook } = {
  void: { glyph: glyph(' '), fg: EARTH.night, bg: EARTH.night },
  grass: {
    glyph: glyph('"'),
    fg: EARTH.moss,
    bg: EARTH.grass,
    variants: [glyph('"'), glyph("'"), glyph('.'), glyph(' ')],
  },
  dirt: { glyph: glyph('.'), fg: EARTH.peat, bg: EARTH.mud, variants: [glyph('.'), glyph(' ')] },
  rock: { glyph: glyph('▒'), fg: EARTH.stone, bg: EARTH.slate, variants: [glyph('▒'), glyph('░')] },
  water: { glyph: glyph('≈'), fg: EARTH.waterlight, bg: EARTH.water },
  reeds: { glyph: glyph('"'), fg: EARTH.reed, bg: EARTH.moss, variants: [glyph('"'), glyph('|')] },
  tree: { glyph: glyph('♣'), fg: EARTH.leaf, bg: EARTH.bark, variants: [glyph('♣'), glyph('↑')] },
  wall: { glyph: glyph('#'), fg: EARTH.bone, bg: EARTH.slate },

  /* ---------------------------------------------------------------------- *
   * M3-01 — the biomes
   * ---------------------------------------------------------------------- */

  sea: { glyph: glyph('≈'), fg: EARTH.water, bg: EARTH.brine },
  shore: {
    glyph: glyph('.'),
    fg: EARTH.shingle,
    bg: EARTH.sand,
    variants: [glyph('.'), glyph(','), glyph(' '), glyph(' ')],
  },
  saltmarsh: {
    glyph: glyph('"'),
    fg: EARTH.reed,
    bg: EARTH.saltgrass,
    variants: [glyph('"'), glyph(','), glyph(' ')],
  },
  meadow: {
    glyph: glyph('"'),
    fg: EARTH.moss,
    bg: EARTH.saltgrass,
    variants: [glyph('"'), glyph("'"), glyph(' '), glyph(' ')],
  },
  ffridd: {
    glyph: glyph('%'),
    fg: EARTH.bracken,
    bg: EARTH.mud,
    variants: [glyph('%'), glyph('"'), glyph(' ')],
  },
  oakwood: {
    glyph: glyph('"'),
    fg: EARTH.leaf,
    bg: EARTH.oak,
    variants: [glyph('"'), glyph(','), glyph(' '), glyph(' ')],
  },
  hazelwood: {
    glyph: glyph(':'),
    fg: EARTH.moss,
    bg: EARTH.hazel,
    variants: [glyph(':'), glyph('"'), glyph(' ')],
  },
  bog: {
    glyph: glyph('~'),
    fg: EARTH.reed,
    bg: EARTH.peatwater,
    variants: [glyph('~'), glyph('"'), glyph(' ')],
  },
  moor: {
    glyph: glyph('"'),
    fg: EARTH.bracken,
    bg: EARTH.heather,
    variants: [glyph('"'), glyph("'"), glyph(' '), glyph(' ')],
  },
  crag: { glyph: glyph('▲'), fg: EARTH.bone, bg: EARTH.stone, variants: [glyph('▲'), glyph('#')] },

  /**
   * The ford. Lighter than the river it interrupts, and drawn with a glyph the
   * river never uses — this is the tile M3-01 asks a player to be able to *find*
   * by walking the bank, so it has to be legible at a glance in a grid of `≈`.
   */
  ford: { glyph: glyph('-'), fg: EARTH.bone, bg: EARTH.waterlight },

  /* ---------------------------------------------------------------------- *
   * M3-02 — the sites
   * ---------------------------------------------------------------------- */

  path: { glyph: glyph('.'), fg: EARTH.sand, bg: EARTH.track, variants: [glyph('.'), glyph(' ')] },
  floor: { glyph: glyph('.'), fg: EARTH.mud, bg: EARTH.peat },
  // Entrances are navigation, not decoration. Match the wall's bone-bright
  // foreground so the `+` remains an obvious break in a `#####` outline after
  // the night grade; bark on peat read as an unbroken wall in the live village.
  door: { glyph: glyph('+'), fg: EARTH.bone, bg: EARTH.peat },
  /**
   * Cold. `SIGNAL.fire` is what a *lit* hearth will be worth when M3-06 gives
   * anybody a way to light one; an unlit ring of stones with the ash banked to
   * one side is not fire, and spending the palette's one warm colour on it now
   * would mean the valley's first campfire had nothing left to say.
   */
  hearth: { glyph: glyph('='), fg: EARTH.stone, bg: EARTH.ash },
  well: { glyph: glyph('o'), fg: EARTH.waterlight, bg: EARTH.slate },

  /**
   * The stones, the Sun Stone and the cauldron are the only terrain allowed a signal colour,
   * and they earn it the way §3.1 says colour is earned: `otherworld` white
   * says *this is not of the valley*, which is precisely what a thin place is.
   * Nothing else on the moor is that bright, so the ring reads from a distance
   * as wrongness rather than as scenery.
   */
  menhir: { glyph: glyph('I'), fg: SIGNAL.otherworld, bg: EARTH.slate },
  cauldron: { glyph: glyph('U'), fg: SIGNAL.otherworld, bg: EARTH.night },
  // A sun-wheel cut into the village's central flagstone. `☼` is part of the
  // shipped CP437 atlas, so the symbol is authored rather than browser-font
  // dependent. Warm fire is deliberately withheld: this is older and colder.
  'sun-stone': { glyph: glyph('☼'), fg: SIGNAL.otherworld, bg: EARTH.peat },

  /**
   * The Meddyg's cot (M4-08). Floor-coloured ground with a bed on it, so it
   * reads as *part of her house* rather than as a thing lying in it — and
   * distinct enough that a player who has been told "the cot by the fire" can
   * find it without asking twice.
   */
  cot: { glyph: glyph('~'), fg: EARTH.bone, bg: EARTH.ash },

  mound: {
    glyph: glyph('"'),
    fg: EARTH.moss,
    bg: EARTH.saltgrass,
    variants: [glyph('"'), glyph('∩')],
  },
  wallow: { glyph: glyph('≈'), fg: EARTH.peat, bg: EARTH.mud, variants: [glyph('≈'), glyph('~')] },
  fence: { glyph: glyph('"'), fg: EARTH.bark, bg: EARTH.mud },

  /**
   * The Meddyg's physic garden (M5-01) — dug rigs up by the well.
   *
   * Turned earth under a growing thing, which is what it is: the `mud` ground
   * says somebody has had a spade to it and the `moss` glyph says something is
   * coming up. It has to read as *tended* rather than as scrub, because the
   * garden is the one patch of ground in the valley that belongs to a person,
   * and a player who cannot tell it from a meadow will forage it flat.
   */
  garden: { glyph: glyph("'"), fg: EARTH.moss, bg: EARTH.mud },
};

/** Lookup by tile id — what arrives on the wire. */
export const TILE_LOOK_BY_ID: readonly TileLook[] = TILE_KINDS.map((kind) => TILE_LOOKS[kind]);

/** Unexplored ground. Not black: black reads as a hole, this reads as absence. */
export const UNEXPLORED: TileLook = {
  glyph: glyph(' '),
  fg: EARTH.night,
  bg: EARTH.night,
};

/**
 * Plants on the ground (M3-03).
 *
 * Drawn as sprites over the terrain rather than as tile glyphs, because a
 * patch is a thing *on* a tile rather than a kind of tile — you stand on the
 * yarrow to pick it, and the ground under it is still meadow.
 *
 * Two looks, not nine. A picked-over patch is dimmed to the point of being
 * scenery, and that is the entire visual feedback the scarcity rule gives:
 * a meadow somebody has worked through looks, at a glance, emptier. Giving
 * each species its own colour would spend the palette's whole budget on
 * decoration and break §3.1 — the one thing colour is allowed to mean here is
 * "there is something here worth stopping for".
 */
/**
 * Marks on the ground (M3-08).
 *
 * Punctuation rather than letters, and quiet colours, because sign is meant to
 * be *noticed* rather than announced — the ticket's "subtle glyph accents", and
 * the whole "the ground is the UI" argument in two lines of table. A trail that
 * lit up would be the minimap arrow this milestone refuses to build.
 *
 * Blood is the exception and is allowed to be the one saturated thing on the
 * terrain layer. It is the mark that means something is hurt, it is the one a
 * hunter is deliberately following, and a desaturated valley is exactly the
 * background against which a little rust reads instantly.
 */
export const SIGN_LOOK = {
  print: { glyph: glyph('"'), fg: 0x6a6152 },
  trail: { glyph: glyph(';'), fg: 0x5f5a3c },
  droppings: { glyph: glyph(':'), fg: 0x4b4034 },
  blood: { glyph: glyph('.'), fg: 0x7a3630 },
} as const;

export const FLORA_LOOK = {
  standing: { glyph: glyph('*'), fg: EARTH.bone },

  /**
   * Picked over: a different **glyph**, not just a dimmer colour.
   *
   * The first version changed only the colour, from `bone` to `saltgrass`, and
   * against a green meadow that is almost no change at all — a player who had
   * just stripped a patch could not tell by looking. Colour alone is the wrong
   * channel for this: the valley is deliberately desaturated (§3.1), so two
   * muted greens are two muted greens.
   *
   * `,` against `*` reads as *cropped* at a glance and at any zoom, which is
   * what the scarcity rule needs to be able to say. The patch stays on the map
   * rather than vanishing, because where the garlic grows is knowledge worth
   * keeping — you come back to it in two days.
   */
  picked: { glyph: glyph(','), fg: EARTH.bracken },
} as const;

/**
 * A grave (M4-07).
 *
 * `+` — a stone set at the head of a mound, and the one mark on this map that
 * nothing will ever remove. In `stone` rather than `bone` so it reads as part
 * of the ground rather than as something lying on it.
 */
export const GRAVE_LOOK = { glyph: glyph('+'), fg: EARTH.stone } as const;

/**
 * A fire burning on the ground (M3-05), and the one place the palette spends
 * its warm colour.
 *
 * `TILE_LOOK.hearth` has been holding this back deliberately since M3-01 — an
 * unlit ring of stones is not fire, and the note there says the valley's first
 * campfire should be what `SIGNAL.fire` is for. This is that campfire.
 *
 * Two states, because the difference is a decision a player has to make:
 * a fire going properly, and one down to **embers**, which still cooks and
 * still keeps the wolves off but wants feeding before it does neither. The
 * embers glyph is a different *character* rather than a dimmer orange, for the
 * reason `FLORA_LOOK.picked` is: against a dark valley two warm colours are two
 * warm colours, and the player has to be able to tell across the green.
 */
export const FIRE_LOOK = {
  burning: { glyph: glyph('^'), fg: SIGNAL.fire },
  embers: { glyph: glyph('"'), fg: 0x8c4a1e },
} as const;

export const ACTOR_LOOK = {
  self: { glyph: glyph('@'), fg: EARTH.bone },
  player: { glyph: glyph('@'), fg: EARTH.iron },
  creature: { glyph: glyph('r'), fg: EARTH.mud },

  /**
   * Somebody who lives here (M4-08). The same `@` as a player, in moss rather
   * than iron: they are people, and the valley should not draw its one healer
   * as a different species — but you should be able to tell across the green
   * which figure is going to answer you.
   */
  villager: { glyph: glyph('@'), fg: EARTH.moss },

  /**
   * A body on the ground — senseless or dead (M2-07).
   *
   * `%` is the roguelike's own word for "remains", and it reads as *fallen* at
   * a glance in a way a rotated `@` cannot in a glyph grid. Dimmed to `mud`
   * because a body is not a thing that acts: the eye should find the people
   * still standing first, and then notice what is lying between them.
   *
   * One look for down *and* dead, deliberately. You cannot tell across a
   * clearing whether the man face-down in the mud is breathing — walking over
   * and looking is the entire drama M2-07 exists to create.
   */
  down: { glyph: glyph('%'), fg: EARTH.mud },

  /** What is left where somebody died. The same mark, colder. */
  corpse: { glyph: glyph('%'), fg: EARTH.night },

  /**
   * Somebody the cauldron gave back (M4-07's Grey-Streaked).
   *
   * The same `@` as anybody else, in bone rather than iron — paler than the
   * living and warmer than a corpse, which is exactly what the mark is. It is
   * the one purely cosmetic thing in the game that is also a mechanic: in a
   * valley of eight people, everybody knowing at a glance who has been dead is
   * a social fact with consequences.
   */
  pale: { glyph: glyph('@'), fg: EARTH.bone },

  /**
   * A dead animal (M3-06) — meat, and the reason to carry a knife.
   *
   * The same `%` as a body, in `bark` rather than `night`: it has to read as
   * *remains* at a glance, and it has to be distinguishable from a person's
   * corpse from across a clearing, because walking twenty seconds to the wrong
   * one is a mistake the glyph grid should not let you make.
   */
  carcass: { glyph: glyph('%'), fg: EARTH.bark },

  /** …and one the crows have finished with. Nothing left worth stopping for. */
  rotten: { glyph: glyph('%'), fg: EARTH.peat },
} as const;

/**
 * The look cursor (M0-06). A crosshair rather than a filled block, so the
 * tile underneath stays readable — the entire point of pointing at it is to
 * see what is there.
 *
 * `otherworld` white is the one saturated colour that is not blood or fire,
 * and a cursor is exactly the kind of "this is the interface talking, not the
 * valley" signal it was reserved for.
 */
export const CURSOR_LOOK = { glyph: glyph('┼'), fg: SIGNAL.otherworld } as const;

/**
 * Dim a colour toward the night, for remembered terrain.
 *
 * Multiplying toward a colour rather than toward black keeps remembered ground
 * looking like ground seen in poor light, which is what it is. Straight
 * multiplication toward black turns everything the same muddy grey and loses
 * the distinction between a remembered wall and remembered water — precisely
 * the thing map memory exists to preserve.
 */
export function dim(colour: Rgb, amount = 0.55, toward: Rgb = EARTH.night): Rgb {
  const t = Math.max(0, Math.min(1, amount));
  return mix(colour, toward, t);
}

/** Linear blend between two packed colours. `t = 0` is `a`, `t = 1` is `b`. */
export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  const k = Math.max(0, Math.min(1, t));

  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;

  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;

  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);

  return (r << 16) | (g << 8) | bl;
}

/** `0xRRGGBB` → `#rrggbb`, for Canvas2D. */
export function css(colour: Rgb): string {
  return `#${(colour >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
}

/* -------------------------------------------------------------------------- *
 * The sky (M3-05)
 * -------------------------------------------------------------------------- */

/**
 * The colours the day is graded through.
 *
 * Dawn amber → noon neutral → dusk violet → night deep blue, which is M3-05's
 * brief spelled as four hex values. Every one of them is a *tint* the cell
 * colour is mixed toward rather than a colour anything is drawn in: the valley
 * keeps its own palette and the sky decides how much of it survives, which is
 * the only way a tint pass can be added to an established look without
 * rewriting it.
 *
 * Note that dawn and dusk are not the same colour. Morning light off the sea is
 * thin and yellow; the last of the evening comes off the moor and goes purple
 * before it goes. Using one colour for both would be cheaper and would lose the
 * one cue that tells a player which end of the night they are at.
 */
export const SKY_TINTS = {
  dawn: 0x8a6a3a,
  noon: 0xbfc4c2,
  dusk: 0x4b3a5c,
  night: 0x0d1524,
} as const;

/** How far toward `night` the deepest midnight pulls a cell. */
export const NIGHT_DEPTH = 0.82;

/** …and how far toward `dawn`/`dusk` the turns of the day pull it. */
export const TWILIGHT_DEPTH = 0.4;

/**
 * Weather sits *over* the time of day: the colour it pulls toward and how hard.
 *
 * Desaturation rather than a hue: rain does not make the valley blue, it makes
 * it grey, and a weather system that tinted every sky a different colour would
 * spend §3.1's whole colour budget on the forecast.
 */
export const WEATHER_TINT = 0x5a6066;

/** What the sky is worth to one cell: a colour to mix toward, and how much. */
export interface Tint {
  readonly colour: Rgb;
  readonly amount: number;
}

export const NO_TINT: Tint = { colour: SKY_TINTS.noon, amount: 0 };

/**
 * The time-of-day half of the tint pass.
 *
 * `daylight` is the server's own curve (0 at midnight, 1 at noon) and `phase`
 * disambiguates the two sides of it — the same 0.5 of light is amber going up
 * and violet coming down, and only the phase knows which.
 */
export function timeTint(daylight: number, phase: 'night' | 'dawn' | 'day' | 'dusk'): Tint {
  const light = Math.max(0, Math.min(1, daylight));

  if (phase === 'day') return { colour: SKY_TINTS.noon, amount: (1 - light) * 0.2 };

  if (phase === 'night') return { colour: SKY_TINTS.night, amount: NIGHT_DEPTH };

  // Through a twilight, the sky's own colour is strongest in the middle of the
  // ramp and gives way to night at one end and to daylight at the other.
  const twilight = SKY_TINTS[phase];
  const night = 1 - light;
  return {
    colour: mix(twilight, SKY_TINTS.night, Math.max(0, night - 0.5) * 2),
    amount: TWILIGHT_DEPTH + night * (NIGHT_DEPTH - TWILIGHT_DEPTH) * 0.8,
  };
}

/**
 * Fold the weather's gloom into a time-of-day tint.
 *
 * Composed by mixing the two tint colours and taking the *remaining* headroom
 * for the weather's share, rather than by adding the amounts: two tints that
 * summed past 1 would black the screen out in a midnight downpour, which is
 * atmospheric for about four seconds and then unplayable.
 */
export function skyTint(base: Tint, gloom: number): Tint {
  const weather = Math.max(0, Math.min(1, gloom));
  if (weather === 0) return base;

  const headroom = 1 - base.amount;
  return {
    colour: mix(base.colour, WEATHER_TINT, weather * 0.6),
    amount: base.amount + headroom * weather * 0.6,
  };
}

/** Apply a tint to a colour. The one place a cell's sky-graded colour is made. */
export function tinted(colour: Rgb, tint: Tint): Rgb {
  return tint.amount <= 0 ? colour : mix(colour, tint.colour, tint.amount);
}
